import { useRef, useState } from 'react';
import {
  FilePageGraphic,
  PlayButtonGraphic,
  RecordingWaveIcon,
} from 'assets/icons';
import { AlbumContentType } from 'common/enums';
import { adaptContentType, classnames } from 'helpers/utils';
import { usePdfFirstPage } from 'hooks/use-pdf-first-page';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import styles from './content-thumbnail.module.scss';

// Media-fragment timestamp: tells the browser to paint the frame at t=10s.
// Same approach as common/album-thumbnail.
const PREVIEW_TIME_SECONDS = 10;

interface ContentThumbnailProps {
  content: AlbumContentSerialized;
  className?: string;
  // Show a play badge over video thumbnails.
  showVideoOverlay?: boolean;
}

// Journal/text bodies are stored as rich-text HTML; render a clean plain-text
// preview by converting block boundaries to line breaks and stripping tags.
const htmlToText = (html: string) => {
  const withBreaks = html
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n');
  const text =
    new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ||
    '';
  return text
    .replace(/\n{2,}/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
};

// Renders a content's thumbnail: images, videos and PDFs show a real preview
// (server thumbnail, image itself, a video frame, or the PDF's first page).
// Otherwise we show a design-system fallback: text entries render a faded
// preview of their body, audio gets the waveform disc, and everything else
// (including a PDF while its page is drawn) gets the folded-page graphic.
export const ContentThumbnail = ({
  content,
  className,
  showVideoOverlay = false,
}: ContentThumbnailProps) => {
  const [videoFailed, setVideoFailed] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const pdfPage = usePdfFirstPage(content, rootRef);
  const type = adaptContentType(content);
  const videoUrl = content.mediaUrl || content.content?.videoUrl || null;

  let media: React.ReactNode = null;
  let isVideo = false;
  let isPdfPage = false;
  // An audio recording's server thumbnail is a frame of its empty picture
  // (black), so audio always gets the waveform disc.
  if (content.thumbnailUrl && type !== AlbumContentType.audio) {
    media = (
      <img
        src={content.thumbnailUrl}
        alt=""
        className={styles.media}
        loading="lazy"
        decoding="async"
      />
    );
    isVideo = type === AlbumContentType.video;
  } else if (type === AlbumContentType.image && content.mediaUrl) {
    media = (
      <img
        src={content.mediaUrl}
        alt=""
        className={styles.media}
        loading="lazy"
        decoding="async"
      />
    );
  } else if (type === AlbumContentType.video && videoUrl && !videoFailed) {
    isVideo = true;
    media = (
      <video
        src={`${videoUrl}#t=${PREVIEW_TIME_SECONDS}`}
        className={styles.media}
        preload="metadata"
        muted
        playsInline
        onError={() => setVideoFailed(true)}
      />
    );
  } else if (pdfPage) {
    isPdfPage = true;
    media = (
      <img
        src={pdfPage}
        alt=""
        className={classnames(styles.media, styles.pdfPage)}
      />
    );
  }

  const isFallback = !media;
  // Text entries keep their body on the underlying content payload.
  const textBody = htmlToText(
    (content.content as unknown as { body?: string })?.body ||
      content.description ||
      '',
  );
  const showTextPreview =
    isFallback && type === AlbumContentType.write && textBody.length > 0;
  const showAudioIcon =
    isFallback && !showTextPreview && type === AlbumContentType.audio;
  // Anything else without a preview — documents and other files.
  const showFilePage = isFallback && !showTextPreview && !showAudioIcon;

  return (
    <span
      ref={rootRef}
      className={classnames(styles.thumb, className ?? '', {
        [styles.fallbackText]: showTextPreview,
        // A PDF's page sits on the same file tile as the page graphic.
        [styles.fallbackFeatured]: showAudioIcon || showFilePage || isPdfPage,
      })}
    >
      {media}
      {showTextPreview && (
        <span className={styles.textPreview}>{textBody}</span>
      )}
      {showAudioIcon && (
        <span className={styles.featuredIcon}>
          <RecordingWaveIcon />
        </span>
      )}
      {showFilePage && <FilePageGraphic className={styles.filePage} />}
      {showVideoOverlay && isVideo && (
        <span className={styles.playOverlay}>
          <PlayButtonGraphic />
        </span>
      )}
    </span>
  );
};
